/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import React, {
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  findStats,
  translateStatsResponse,
  separateUpperAndLowerPools,
} from '@sportninja/common/utils/stats'
import { useQueryState } from '../utils'
import BracketFilter from './BracketFilter'
import Brackets from './Brackets'
import { EmptySchedule } from '../EmptySchedule'
import Text from 'src/components/ts-components/Text'
import { FullscreenOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import useWindowSize from 'src/hooks/useWindowSize'

const searchControlsStyles = {
  container: css`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    width: 100%;
  `,
  leftControls: css`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  `,
  rightControls: css`
    margin-top: 16px;
  `,
  searchInput: css`
    width: 300px;
    color: ${colors.WHITE};
    font-size: 14px;
    font-weight: 400;
    line-height: 20.3px;
    border-radius: 8px;
    border: 1px solid ${colors.SECONDARY_100};
    background: ${colors.SECONDARY_200};
    height: 36px !important;
    @media (max-width: 768px) {
      width: 100%;
    }
    .ant-input {
      background-color: ${colors.SECONDARY_500};
      color: ${colors.WHITE};
    }
    .ant-input-prefix {
      color: ${colors.NEUTRAL_100};
    }
    &::placeholder {
      color: ${colors.NEUTRAL_100};
    }
  `,
  expandAllButton: css`
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid ${colors.SECONDARY_200};
    background: ${colors.SECONDARY_300};
    height: 36px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    &:hover {
      border-color: ${colors.PRIMARY};
    }
  `,
  switchContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid ${colors.SECONDARY_200};
    background: ${colors.SECONDARY_300};
    height: 36px !important;
    cursor: pointer;
  `,
}

const ScheduleBracket = ({
  id,
  canUpdate,
  setBracketScheduleId,
  setBracketDivision,
  setIsFullScreenBracketModalOpen,
  setBracketCompetitionType,
  isFullScreenBracketModalOpen = false,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const parentRef = useRef(null)
  const innerRef = useRef(null)
  const [loadingFilter, setLoadingFilter] = useState(true)
  const [loadingBracket, setLoadingBracket] = useState(true)
  const [selected, setSelected] = useState({})
  const [rounds, setRounds] = useState([])
  const [competitionType, setCompetitionType] = useState(
    t('common:competition')
  )
  const [comps, setComps] = useState([])
  const [selectedComp, setSelectedComp] = useQueryState('sn_division', '')
  const [stats, setStats] = useState({})

  const hasAutoSelected = useRef(selectedComp)

  const handle = (response) => {
    let finalRounds = response?.data.map((round) => {
      const pools = round?.pools?.data?.map((pool) => {
        const ref = createRef(null)
        return {
          ref,
          ...pool,
        }
      })
      return {
        ...round,
        pools,
      }
    })
    finalRounds = separateUpperAndLowerPools(finalRounds)
    setRounds(finalRounds)
  }

  /*
    Store an object like this:
    {
      "pool1_id": {
        "team1_id": {
          "scoreString": "0-0-1",
          "points": 1
        },
        "team2_id": {
          "scoreString": "0-0-1",
          "points": 1
        }
      }
    }
  */
  const handleStats = (response) => {
    if (!response?.data) {
      return
    }
    const aggregatedStats = translateStatsResponse(response)
    const teamsByPool = {}
    aggregatedStats.forEach((teamPerPool) => {
      const { team, stats, pool } = teamPerPool
      const wins = findStats(stats, 'win')
      const losses = findStats(stats, 'loss')
      const ties = findStats(stats, 'tie')
      const points = findStats(stats, 'points')
      teamsByPool[pool.id] = Object.assign({}, teamsByPool[pool.id], {
        [team.id]: {
          scoreString: `${wins}-${losses}-${ties}`,
          points,
        },
      })
    })

    setStats(teamsByPool)
  }

  useEffect(() => {
    const init = async () => {
      setLoadingFilter(true)
      try {
        const { data: compStructure } = await req(
          `/schedules/${id}/children/dropdown`
        )
        const levelContainingTeams = compStructure[compStructure.length - 1]

        if (levelContainingTeams) {
          setBracketCompetitionType(levelContainingTeams.name)
          setCompetitionType(levelContainingTeams.name)
          setComps(levelContainingTeams.schedules || [])
        }
      } finally {
        setLoadingFilter(false)
      }
    }
    init()
  }, [id])

  useEffect(() => {
    if (
      !loadingFilter &&
      !selectedComp &&
      comps.length > 0 &&
      !hasAutoSelected.current
    ) {
      setSelectedComp(comps[0].id)
      hasAutoSelected.current = true
    }
  }, [selectedComp, loadingFilter, comps])

  useEffect(() => {
    const combined = async () => {
      setLoadingBracket(true)
      try {
        const results = await Promise.all([
          await req(`/schedules/${selectedComp}/tournaments`),
          await req(`/schedules/${selectedComp}/tournaments/stats`),
        ])
        typeof results[0] !== 'undefined' && handle(results[0])
        typeof results[1] !== 'undefined' && handleStats(results[1])
      } finally {
        setLoadingBracket(false)
      }
    }
    if (selectedComp.length > 0) combined()
  }, [selectedComp])

  const checkScrollRoom = useCallback(() => {
    const availableWidth = parentRef?.current?.clientWidth
    const innerWidth = innerRef?.current?.clientWidth

    if (availableWidth < innerWidth) {
      parentRef?.current?.classList.add('show-right-fade')
    } else {
      parentRef?.current?.classList.remove('show-right-fade')
    }
  }, [])

  useEffect(() => {
    if (!loadingBracket) {
      checkScrollRoom()
    }
  }, [checkScrollRoom, loadingBracket])

  useEffect(() => {
    window.addEventListener('resize', checkScrollRoom)

    return () => {
      window.removeEventListener('resize', checkScrollRoom)
    }
  }, [checkScrollRoom])

  const onScroll = useCallback((e) => {
    const availableWidth = parentRef?.current?.clientWidth
    const innerWidth = innerRef?.current?.clientWidth

    const left = e.target.scrollLeft
    const availableScroll = innerWidth - availableWidth

    if (availableWidth > innerWidth) {
      parentRef?.current?.classList.remove('show-left-fade')
      parentRef?.current?.classList.remove('show-right-fade')
    } else if (left >= availableScroll) {
      e.target.classList.remove('show-right-fade')
      e.target.classList.add('show-left-fade')
    } else if (left === 0) {
      e.target.classList.remove('show-left-fade')
      e.target.classList.add('show-right-fade')
    } else if (left !== 0 || left < availableScroll) {
      e.target.classList.add('show-right-fade')
      e.target.classList.add('show-left-fade')
    }
  }, [])

  return (
    <Fragment>
      <>
        <div
          css={css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 16px;
            border-bottom: 1px solid ${colors.NEUTRAL_200};
            margin-bottom: 16px;
          `}
        >
          <BracketFilter
            comps={comps}
            setSelectedComp={(c) => {
              setSelectedComp(c.id)
            }}
            selectedComp={selectedComp}
            competitionType={competitionType}
          />
          <div
            css={[
              searchControlsStyles.rightControls,
              css`
                ${isMobile && 'display: none;'}
              `,
            ]}
          >
            <div
              css={[
                searchControlsStyles.expandAllButton,
                css`
                  opacity: ${loadingBracket || rounds.length === 0 ? 0.5 : 1};
                  cursor: ${loadingBracket || rounds.length === 0
                    ? 'not-allowed'
                    : 'pointer'};
                `,
              ]}
              onClick={() => {
                // loadingBracket || rounds.length === 0
                if (loadingBracket || rounds.length === 0) {
                  return
                }
                if (selectedComp && !loadingBracket && rounds.length > 0) {
                  const division = comps.find(
                    (comp) => comp.id === selectedComp
                  )
                  setBracketScheduleId(selectedComp)
                  setBracketDivision(division)
                  setIsFullScreenBracketModalOpen(true)
                }
              }}
            >
              <FullscreenOutlined
                css={css`
                  font-size: 16px;
                  color: ${colors.WHITE};
                `}
              />
              <Text
                variant='paragraphSmall'
                color={colors.WHITE}
                weight='regular'
              >
                Full Screen
              </Text>
            </div>
          </div>
        </div>
      </>
      {rounds.length === 0 && !loadingBracket ? (
        <EmptySchedule isPlayoff scheduleId={id} canUpdate={canUpdate} />
      ) : (
        <>
          {isFullScreenBracketModalOpen ? null : (
            <Brackets
              onScroll={onScroll}
              loadingBracket={loadingBracket}
              competitionType={competitionType}
              innerRef={innerRef}
              parentRef={parentRef}
              selectedComp={selectedComp}
              rounds={rounds}
              selected={selected}
              setSelected={setSelected}
              stats={stats}
              isOnModal={false}
            />
          )}
        </>
      )}
    </Fragment>
  )
}

export default ScheduleBracket
